import { fetchAuthSession } from '@aws-amplify/auth'
import { jwtDecode } from 'jwt-decode'
import { getAuthStorage } from './tokenStorage'

const ACCESS_TOKEN_KEY = 'tbd_access_token'
const ID_TOKEN_KEY = 'tbd_id_token'
const LEGACY_TOKEN_KEY = 'loggedInToken'
const EXPIRY_SKEW_SECONDS = 60

const storage = getAuthStorage()

const memoryCache = {
  access: null,
  id: null,
}

const nowSeconds = () => Math.floor(Date.now() / 1000)

const isExpired = exp => !exp || exp <= nowSeconds() + EXPIRY_SKEW_SECONDS

const safeParse = raw => {
  try {
    return JSON.parse(raw)
  } catch (err) {
    return null
  }
}

const readStorage = key => {
  if (!storage) return null
  try {
    return storage.getItem(key)
  } catch (err) {
    return null
  }
}

const writeStorage = (key, value) => {
  if (!storage) return
  try {
    if (value === null || value === undefined) {
      storage.removeItem(key)
    } else {
      storage.setItem(key, value)
    }
  } catch (err) {
    // ignore storage write failures
  }
}

const normalizeToken = tokenObj => {
  if (!tokenObj) return null
  if (typeof tokenObj === 'string') return tokenObj
  if (typeof tokenObj.toString === 'function') return tokenObj.toString()
  return null
}

const getTokenExp = tokenObj => {
  if (tokenObj?.payload?.exp) return tokenObj.payload.exp
  const token = normalizeToken(tokenObj)
  if (!token) return null
  try {
    const decoded = jwtDecode(token)
    return decoded?.exp || null
  } catch (err) {
    return null
  }
}

const getStorageKey = type => (type === 'access' ? ACCESS_TOKEN_KEY : ID_TOKEN_KEY)

const hydrateFromStorage = type => {
  const storageKey = getStorageKey(type)
  const raw = readStorage(storageKey)
  if (!raw) return null

  const parsed = safeParse(raw)
  if (parsed?.token) {
    if (isExpired(parsed.exp)) {
      writeStorage(storageKey, null)
      return null
    }
    memoryCache[type] = parsed
    return parsed
  }

  // Legacy raw token string in sessionStorage
  if (typeof raw === 'string') {
    const exp = getTokenExp(raw)
    if (isExpired(exp)) {
      writeStorage(storageKey, null)
      return null
    }
    const record = { token: raw, exp }
    memoryCache[type] = record
    writeStorage(storageKey, JSON.stringify(record))
    return record
  }

  return null
}

const getCachedToken = type => {
  const cached = memoryCache[type]
  if (cached?.token && !isExpired(cached.exp)) {
    return cached.token
  }
  const hydrated = hydrateFromStorage(type)
  return hydrated?.token || null
}

const cacheToken = (type, token, exp) => {
  const storageKey = getStorageKey(type)
  if (!token) {
    memoryCache[type] = null
    writeStorage(storageKey, null)
    return
  }
  const record = { token, exp }
  memoryCache[type] = record
  writeStorage(storageKey, JSON.stringify(record))
}

const refreshTokens = async () => {
  const session = await fetchAuthSession()
  const accessTokenObj = session?.tokens?.accessToken
  const idTokenObj = session?.tokens?.idToken

  const accessToken = normalizeToken(accessTokenObj)
  const idToken = normalizeToken(idTokenObj)

  const accessExp = getTokenExp(accessTokenObj)
  const idExp = getTokenExp(idTokenObj)

  cacheToken('access', accessToken, accessExp)
  cacheToken('id', idToken, idExp)

  return { accessToken, idToken }
}

// One-time migration of the pre-CSP legacy token into the new token cache.
// NOTE: this only seeds the ID-token slot — there is no legacy access token to
// restore. Because API auth now requires the access token (see getAccessToken),
// a legacy-token user whose Amplify session cannot be refreshed has no access
// token and will be fail-closed: the next secure call throws AUTH_TOKEN_MISSING
// and the app redirects to /login. This is intentional (no silent
// unauthenticated access); affected users simply re-authenticate once.
const migrateLegacyToken = () => {
  if (!storage) return
  const existingId = readStorage(ID_TOKEN_KEY)
  if (existingId) return

  const legacySession = readStorage(LEGACY_TOKEN_KEY)
  if (legacySession) {
    cacheToken('id', legacySession, getTokenExp(legacySession))
    writeStorage(LEGACY_TOKEN_KEY, null)
    return
  }

  try {
    const legacyLocal = localStorage.getItem(LEGACY_TOKEN_KEY)
    if (legacyLocal) {
      cacheToken('id', legacyLocal, getTokenExp(legacyLocal))
      localStorage.removeItem(LEGACY_TOKEN_KEY)
    }
  } catch (err) {
    // ignore localStorage access failures
  }
}

migrateLegacyToken()

export const getAccessToken = async () => {
  const cached = getCachedToken('access')
  if (cached) return cached
  try {
    const { accessToken } = await refreshTokens()
    return accessToken || null
  } catch (err) {
    return null
  }
}

export const getIdToken = async () => {
  const cached = getCachedToken('id')
  if (cached) return cached
  try {
    const { idToken } = await refreshTokens()
    return idToken || null
  } catch (err) {
    return null
  }
}

// Decodes any JWT string (e.g. an access token) into its claims. Returns null
// for missing or malformed input. Use this when you already hold a token string
// and need to verify it / read its claims — e.g. session-validity gating on `sub`.
export const decodeToken = token => {
  if (!token) return null
  try {
    return jwtDecode(token)
  } catch (err) {
    return null
  }
}

// Decodes the cached *ID token* claims (given_name, email, name, etc.). Takes no
// argument by design: passing an access token here previously "worked" for
// `sub`/truthiness checks but silently lacked ID-token profile claims, which is a
// foot-gun. Callers validating a session from an access token should call
// `decodeToken(accessToken)` instead.
export const decodeIdToken = () => decodeToken(getCachedToken('id'))

export const clearCachedTokens = () => {
  memoryCache.access = null
  memoryCache.id = null
  writeStorage(ACCESS_TOKEN_KEY, null)
  writeStorage(ID_TOKEN_KEY, null)
  writeStorage(LEGACY_TOKEN_KEY, null)
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY)
  } catch (err) {
    // ignore localStorage access failures
  }
}
